package app.footyguide.web.data

import kotlinx.coroutines.await
import kotlin.js.Promise

/**
 * Firestore REST API client for Kotlin/JS.
 *
 * Usa fetch() nativa do browser — Promise nativa, sem módulo dynamic import.
 * Para collection group queries (runQuery), faz auth anônimo via Firebase Auth REST API.
 */
object FirestoreSdk {

    internal var fetchProvider: dynamic = null

    private fun config() = js("window.__FOOTYGUIDE_CONFIG__")

    private fun baseUrl(): String {
        val cfg = config()
        if (cfg == null || cfg == undefined) error("__FOOTYGUIDE_CONFIG__ nao encontrado")
        val project = cfg.firebaseConfig.projectId as String
        return "projects/$project/databases/${FirestoreDatabase.configuredId()}"
    }

    private fun apiBase(): String {
        val cfg = config()
        return if (cfg.useEmulator == true) {
            val host = cfg.emulatorHost as? String ?: "localhost"
            val port = (cfg.emulatorPort as? Number)?.toInt() ?: 8181
            "http://$host:$port/v1"
        } else "https://firestore.googleapis.com/v1"
    }

    private fun fetchFn(): dynamic = fetchProvider ?: js("globalThis.fetch.bind(globalThis)")

    /** Busca documento único (ex: "teams/flamengo"). */
    suspend fun document(path: String): SdkDocument? {
        val url = "${apiBase()}/${baseUrl()}/documents/$path"
        val json = getJson(url, requiresAuth = true) ?: return null
        return restDocToSdk(json)
    }

    /** Busca todos docs de uma collection (ex: "teams"). */
    suspend fun collection(path: String): List<SdkDocument> {
        val docs = mutableListOf<SdkDocument>()
        var pageToken: String? = null

        do {
            val url = collectionUrl(path, pageToken)
            val json = getJson(url, requiresAuth = true) ?: break
            val pageDocs = json.documents
            if (pageDocs != null && pageDocs != undefined) {
                docs.addAll(parseDocList(pageDocs))
            }
            pageToken = json.nextPageToken as? String
        } while (!pageToken.isNullOrBlank())

        return docs
    }

    /** Collection group query com where/orderBy/limit. Requer auth anônimo. */
    suspend fun runQuery(query: StructuredQuery): List<SdkDocument> {
        val url = "${apiBase()}/${baseUrl()}/documents:runQuery"
        val body = buildQueryBody(query)
        val json = postJson(url, body, requiresAuth = true) ?: return emptyList()
        val results = mutableListOf<SdkDocument>()
        val len = (json.length as? Number)?.toInt() ?: 0
        for (i in 0 until len) {
            val entry = json[i]
            if (entry.document != null && entry.document != undefined) {
                val doc = restDocToSdk(entry.document)
                if (doc != null) results.add(doc)
            }
        }
        return results
    }

    /** Set/merge document — requer auth. Nao implementado. */
    suspend fun setDocument(path: String, fields: Map<String, Any?>, merge: Boolean = true) {
        error("[FirestoreSdk] setDocument requer autenticacão")
    }

    // ── HTTP helpers ───────────────────────────────────────────

    private suspend fun getJson(url: String): dynamic? {
        return getJson(url, requiresAuth = false)
    }

    private fun allowUnauthenticatedLocalhostFallback(): Boolean {
        return js(
            """
            (typeof window !== 'undefined' &&
             window.__footyguideAuth === undefined &&
             window.location &&
             (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
             window.location.port === '8090')
            """
        ) as Boolean
    }

    private suspend fun getJson(url: String, requiresAuth: Boolean): dynamic? {
        return try {
            val init = js("({ method: 'GET', headers: {} })")
            if (requiresAuth) {
                val token = FirebaseSession.currentIdToken()
                if (token != null) {
                    init.headers["Authorization"] = "Bearer $token"
                } else if (!allowUnauthenticatedLocalhostFallback()) {
                    return null
                }
            }
            val resp = (fetchFn()(url, init) as Promise<dynamic>).await()
            if (resp.ok != true) return null
            (resp.json() as Promise<dynamic>).await()
        } catch (_: Exception) { null }
    }

    private fun collectionUrl(path: String, pageToken: String?): String {
        val token = pageToken?.takeIf { it.isNotBlank() }?.let {
            "&pageToken=${js("encodeURIComponent")(it)}"
        } ?: ""
        return "${apiBase()}/${baseUrl()}/documents/$path?pageSize=300$token"
    }

    private suspend fun postJson(url: String, body: String, requiresAuth: Boolean = false): dynamic? {
        return try {
            val init = js("({ method: 'POST', headers: { 'Content-Type': 'application/json' } })")
            if (requiresAuth) {
                val token = FirebaseSession.currentIdToken()
                if (token != null) {
                    init.headers["Authorization"] = "Bearer $token"
                } else if (!allowUnauthenticatedLocalhostFallback()) {
                    return null
                }
            }
            init.body = body
            val resp = (fetchFn()(url, init) as Promise<dynamic>).await()
            if (resp.ok != true) {
                val errorBody = (resp.text() as Promise<dynamic>).await()
                console.error("[FirestoreSdk] POST ${url.split("/").last()} ${resp.status}: $errorBody")
                return null
            }
            (resp.json() as Promise<dynamic>).await()
        } catch (_: Exception) { null }
    }

    // ── REST → SdkDocument ─────────────────────────────────────

    private fun restDocToSdk(doc: dynamic): SdkDocument? {
        val name = doc.name as? String ?: return null
        val id = name.substringAfterLast("/")
        val flat = unwrapFields(doc)
        return SdkDocument(id, flat)
    }

    private fun unwrapFields(doc: dynamic): dynamic {
        val fields = doc.fields ?: return doc
        val result = js("{}")
        val keys = js("Object.keys")(fields) as Array<String>
        for (key in keys) {
            result[key] = unwrapField(fields[key])
        }
        return result
    }

    private fun unwrapField(f: dynamic): dynamic {
        if (f == null || f == undefined) return null
        if (f.stringValue != null) return f.stringValue as String
        if (f.integerValue != null) return (f.integerValue as String).toIntOrNull() ?: 0
        if (f.doubleValue != null) return f.doubleValue as Double
        if (f.booleanValue != null) return f.booleanValue as Boolean
        if (f.nullValue != null) return null
        if (f.arrayValue != null) {
            val vals = f.arrayValue.values
            if (vals == null || vals == undefined) return js("[]")
            val arr = js("[]")
            for (i in 0 until (vals.length as Int)) { arr[i] = unwrapField(vals[i]) }
            return arr
        }
        if (f.mapValue != null) return unwrapFields(f.mapValue)
        return null
    }

    // ── StructuredQuery → JSON body ────────────────────────────

    private fun buildQueryBody(q: StructuredQuery): String {
        val from = if (q.allDescendants)
            """{"collectionId":"${q.collectionId}","allDescendants":true}"""
        else
            """{"collectionId":"${q.collectionId}"}"""
        val where = buildWhere(q)
        val order = buildOrder(q)
        val limit = q.limitCount?.let { ""","limit":$it""" } ?: ""
        return """{"structuredQuery":{"from":[$from]$where$order$limit}}"""
    }

    private fun buildWhere(q: StructuredQuery): String {
        if (q.wheres.isEmpty()) return ""
        val filters = q.wheres.joinToString(",") { w ->
            val op = mapOp(w.op)
            val v = valueJson(w.value)
            """{"fieldFilter":{"field":{"fieldPath":"${w.field}"},"op":"$op","value":$v}}"""
        }
        return if (q.wheres.size == 1) ""","where":$filters"""
               else ""","where":{"compositeFilter":{"op":"AND","filters":[$filters]}}"""
    }

    private fun buildOrder(q: StructuredQuery): String {
        if (q.orders.isEmpty()) return ""
        val orders = q.orders.joinToString(",") { o ->
            val d = if (o.direction == "desc") "DESCENDING" else "ASCENDING"
            """{"field":{"fieldPath":"${o.field}"},"direction":"$d"}"""
        }
        return ""","orderBy":[$orders]"""
    }

    private fun mapOp(op: String): String = when (op) {
        "==" -> "EQUAL"; "!=" -> "NOT_EQUAL"
        ">" -> "GREATER_THAN"; ">=" -> "GREATER_THAN_OR_EQUAL"
        "<" -> "LESS_THAN"; "<=" -> "LESS_THAN_OR_EQUAL"
        "array-contains" -> "ARRAY_CONTAINS"
        "in" -> "IN"; "not-in" -> "NOT_IN"
        "array-contains-any" -> "ARRAY_CONTAINS_ANY"
        else -> op.uppercase()
    }

    private fun valueJson(v: Any): String = when (v) {
        is String -> """{"stringValue":"${esc(v)}"}"""
        is Boolean -> """{"booleanValue":$v}"""
        is Number -> """{"integerValue":$v}"""
        else -> """{"nullValue":null}"""
    }

    private fun esc(s: String) = s.replace("\\", "\\\\").replace("\"", "\\\"")

    private fun parseDocList(docs: dynamic): List<SdkDocument> {
        val result = mutableListOf<SdkDocument>()
        val len = (docs.length as? Number)?.toInt() ?: 0
        for (i in 0 until len) { val d = restDocToSdk(docs[i]); if (d != null) result.add(d) }
        return result
    }
}
